import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Globe2, Copy, CheckCircle2, ExternalLink,
  Mail, Sparkles, Loader2, Check, Plus, X, ShieldCheck, Zap, PartyPopper
} from "lucide-react";

// Friendly destination options (matches free inbox providers)
const DESTINATIONS = [
  { id: "gmail", label: "Gmail", pattern: "@gmail.com", helpUrl: "https://mail.google.com/mail/u/0/#settings/accounts" },
  { id: "yahoo", label: "Yahoo", pattern: "@yahoo.com", helpUrl: "https://help.yahoo.com/kb/SLN3523.html" },
  { id: "outlook", label: "Outlook", pattern: "@outlook.com", helpUrl: "https://outlook.office.com/mail/options/mail/accounts" },
  { id: "icloud", label: "iCloud", pattern: "@icloud.com", helpUrl: "https://support.apple.com/guide/icloud/set-up-a-custom-email-domain-mmda0f5f49b3/icloud" },
  { id: "other", label: "Something else", pattern: "", helpUrl: "" },
];

const DEFAULT_ALIASES = ["info", "hello", "support", "sales", "contact"];

const LOADING_STEPS = [
  { ms: 0,    label: "Creating your email addresses…" },
  { ms: 900,  label: "Setting up secure delivery…" },
  { ms: 1800, label: "Optimizing deliverability…" },
  { ms: 2700, label: "Almost there…" },
];

function detectProvider(email) {
  if (!email) return null;
  const e = email.toLowerCase();
  for (const d of DESTINATIONS) {
    if (d.pattern && e.endsWith(d.pattern)) return d.id;
  }
  return null;
}

function hostRelative(name, domain) {
  if (!name) return "@";
  const lower = name.toLowerCase();
  if (lower === domain) return "@";
  if (lower.endsWith("." + domain)) return lower.slice(0, -(domain.length + 1));
  return name;
}

function CopyRow({ label, value, testId }) {
  const [copied, setCopied] = useState(false);
  return (
    <button type="button" onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      data-testid={testId}
      className="w-full flex items-center justify-between gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 text-left group">
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
        <div className="mono text-sm text-[#0F172A] truncate">{value}</div>
      </div>
      {copied
        ? <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0"/>
        : <Copy className="h-4 w-4 text-slate-400 group-hover:text-[#F26953] flex-shrink-0"/>}
    </button>
  );
}

export default function AddDomain() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, config } = useAuth();

  // Hard gate: free-plan users must pick a paid plan before adding a domain.
  useEffect(() => {
    if (user && typeof user === "object" && user.role !== "admin" && user.plan === "free") {
      nav("/#pricing", { replace: true });
    }
  }, [user, nav]);

  // Each domain carries its own plan tier. Priority: ?tier= query param (from the
  // plan-picker modal redirect) → user's main plan → starter fallback.
  const requestedTier = (searchParams.get("tier") || "").toLowerCase();
  const defaultTier = ["starter", "growth", "pro"].includes(requestedTier)
    ? requestedTier
    : (user?.role === "admin" ? "pro" : (user?.plan || "starter"));
  const [domainPlanTier, setDomainPlanTier] = useState(defaultTier);
  useEffect(() => { setDomainPlanTier(defaultTier); }, [defaultTier]);

  // Steps: 1=promise, 1.5=inline-find-domain, 2=domain, 3=aliases, 4=destination, 5=loading, 6=success, 7=dns-fallback
  const [step, setStep] = useState(1);

  // Form state
  const [domain, setDomain] = useState("");
  const [aliasChips, setAliasChips] = useState(() => new Set(["info"]));
  const [customAlias, setCustomAlias] = useState("");
  const userProviderGuess = useMemo(() => detectProvider(user?.email), [user?.email]);
  const [destinationId, setDestinationId] = useState(userProviderGuess || "gmail");
  const [destinationEmail, setDestinationEmail] = useState(user?.email || "");

  // Inline domain-finder (step 1.5)
  const [finderQuery, setFinderQuery] = useState("");
  const [finderResults, setFinderResults] = useState(null);
  const [finderLoading, setFinderLoading] = useState(false);
  const [finderSource, setFinderSource] = useState(null);

  // Plan-limit awareness for alias step — uses THIS domain's chosen tier.
  const tierConf = config?.plans?.find((p) => p.id === domainPlanTier);
  const aliasLimit = user?.role === "admin" ? 9999 : (tierConf?.max_aliases ?? 1);
  const planLabel = (tierConf?.name || domainPlanTier).toString();

  // Provisioning state
  const [loadingStepIdx, setLoadingStepIdx] = useState(0);
  const [createdDomainId, setCreatedDomainId] = useState(null);
  const [createdAliases, setCreatedAliases] = useState([]);
  const [dnsInfo, setDnsInfo] = useState(null);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (user?.email && !destinationEmail) setDestinationEmail(user.email);
  }, [user?.email, destinationEmail]);

  const toggleAlias = (a) => {
    setAliasChips((prev) => {
      const n = new Set(prev);
      if (n.has(a)) n.delete(a); else n.add(a);
      return n;
    });
  };

  const addCustomAlias = () => {
    const v = customAlias.trim().toLowerCase().replace(/[^a-z0-9._-]/g, "");
    if (v.length > 0) {
      setAliasChips((prev) => new Set([...prev, v]));
      setCustomAlias("");
    }
  };

  // Step 5 — provisioning
  useEffect(() => {
    if (step !== 5) return;
    let cancelled = false;
    setLoadingStepIdx(0);

    // Rotating status messages (UX only)
    const timers = LOADING_STEPS.map((s, i) =>
      setTimeout(() => { if (!cancelled) setLoadingStepIdx(i); }, s.ms)
    );

    (async () => {
      try {
        // 1. Create domain (tagged with the selected per-domain plan tier)
        const { data: dom } = await api.post("/domains", {
          domain: domain.toLowerCase().trim(),
          provider: "other",
          plan_tier: domainPlanTier,
        });
        if (cancelled) return;
        setCreatedDomainId(dom.id);

        // 2. Create all selected aliases (respecting plan limit — silently cap, warning already shown in step 3)
        const aliasesToCreate = Array.from(aliasChips).slice(0, user?.role === "admin" ? 9999 : aliasLimit);
        const created = [];
        for (const a of aliasesToCreate) {
          try {
            const { data } = await api.post("/aliases", {
              domain_id: dom.id,
              alias: a,
              destinations: [destinationEmail],
            });
            created.push(data);
          } catch (err) {
            // Silent — we'll show warning in success if any failed
            console.warn(`alias ${a} failed`, err);
          }
        }
        if (cancelled) return;
        setCreatedAliases(created);

        // 3. Try to auto-verify
        let verified = false;
        try {
          await api.post(`/domains/${dom.id}/verify`);
          verified = true;
        } catch {
          verified = false;
        }
        if (cancelled) return;

        // 4. Brief minimum loader so UX feels intentional
        await new Promise((r) => setTimeout(r, 800));
        if (cancelled) return;

        if (verified) {
          setStep(6); // success
        } else {
          // Pull DNS records and go to friendly DNS fallback
          try {
            const { data: dns } = await api.get(`/domains/${dom.id}/dns`);
            setDnsInfo(dns);
          } catch { /* ignore */ }
          setStep(7); // DNS fallback
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(formatApiError(err));
          setStep(2);
        }
      }
    })();

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const retryVerify = async () => {
    if (!createdDomainId) return;
    setRetrying(true);
    try {
      const { data } = await api.post(`/domains/${createdDomainId}/verify`);
      if (data?.sending_valid === false && data?.receiving_valid === true) {
        toast.success(data.message || "Connected! Your email is live.");
      } else {
        toast.success("Connected! Your email is live.");
      }
      setStep(6);
    } catch (e) {
      toast.error(formatApiError(e));
      // Re-pull DNS (records sometimes change)
      try {
        const { data } = await api.get(`/domains/${createdDomainId}/dns`);
        setDnsInfo(data);
      } catch { /* ignore */ }
    } finally {
      setRetrying(false);
    }
  };

  const progressPct = Math.min(100, (step / 6) * 100);
  const dest = DESTINATIONS.find((d) => d.id === destinationId) || DESTINATIONS[0];
  const firstAlias = Array.from(aliasChips)[0] || "info";
  const primaryEmail = domain ? `${firstAlias}@${domain.toLowerCase()}` : "";

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <main className="max-w-2xl mx-auto px-6 lg:px-8 py-12" data-testid="onboard-wizard">
        {step < 5 && (
          <Link to="/dashboard" data-testid="back-to-dashboard" className="inline-flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#0F172A] mb-6">
            <ArrowLeft className="h-4 w-4"/> Back to dashboard
          </Link>
        )}

        {/* Progress bar */}
        {step <= 6 && (
          <div className="mb-8">
            <div className="h-1 rounded-full bg-slate-200 overflow-hidden">
              <div className="h-full bg-[#F26953] transition-all duration-500" style={{ width: `${progressPct}%` }}/>
            </div>
            <div className="mt-2 text-[11px] uppercase tracking-[0.15em] font-bold text-slate-400">
              {step === 1 && "Step 1 of 5 · Welcome"}
              {step === 2 && "Step 2 of 5 · Your domain"}
              {step === 3 && "Step 3 of 5 · Your addresses"}
              {step === 4 && "Step 4 of 5 · Destination"}
              {step === 5 && "Step 5 of 5 · Setting up"}
              {step === 6 && "Done!"}
              {step === 7 && "One final step"}
            </div>
          </div>
        )}

        <div className="bg-white rounded-3xl border border-[#E2E8F0] shadow-sm overflow-hidden">
          {/* STEP 1 — Promise */}
          {step === 1 && (
            <div className="p-10 md:p-14 text-center space-y-6" data-testid="onboard-step-welcome">
              <div className="h-16 w-16 rounded-2xl bg-[#FEE7E1] grid place-items-center mx-auto">
                <Sparkles className="h-8 w-8 text-[#F26953]"/>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-[#0F172A] tracking-tight">
                Let's get your professional email set up.
              </h1>
              <p className="text-base text-[#475569] max-w-md mx-auto">
                About 2 minutes from now, you'll have <span className="mono text-[#F26953] font-semibold">hello@yourdomain.com</span> forwarding straight to the inbox you already use.
              </p>
              <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={() => setStep(2)}
                  data-testid="onboard-have-domain"
                  className="rounded-xl border-2 border-[#F26953] bg-[#F26953] text-white font-semibold p-5 hover:bg-[#D85340] transition-colors"
                >
                  <Globe2 className="h-5 w-5 mx-auto mb-2"/>
                  I already own a domain
                </button>
                <button
                  onClick={() => setStep(1.5)}
                  data-testid="onboard-need-domain"
                  className="rounded-xl border-2 border-[#E2E8F0] bg-white text-[#0F172A] font-semibold p-5 hover:border-[#F26953]/40 hover:bg-[#FFF8F6] transition-colors"
                >
                  <Sparkles className="h-5 w-5 mx-auto mb-2 text-[#F26953]"/>
                  I need a domain
                </button>
              </div>
              <p className="text-[11px] text-slate-400 pt-2">Already using Gmail, Yahoo, or Outlook? Perfect — you keep using it, we handle the rest.</p>
            </div>
          )}

          {/* STEP 1.5 — Inline domain finder (when user needs a domain) */}
          {step === 1.5 && (
            <div className="p-10 space-y-6" data-testid="onboard-step-find-domain">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A] tracking-tight">Let's find you a domain.</h1>
                <p className="text-sm text-[#475569] mt-2">Type your business name — we'll suggest brand-ready options across <span className="font-semibold text-[#0F172A]">.com</span>, <span className="font-semibold text-[#0F172A]">.co</span>, and <span className="font-semibold text-[#0F172A]">.io</span> with live availability. Pick one and we'll send you to Dynadot to grab it.</p>
              </div>

              <form onSubmit={async (e) => {
                e.preventDefault();
                const q = finderQuery.trim();
                if (q.length < 2) { toast.error("Enter at least 2 characters"); return; }
                setFinderLoading(true);
                try {
                  const { data } = await api.get("/domain-search", { params: { query: q } });
                  setFinderResults(data.results || []);
                  setFinderSource(data.using_real_api ? "rdap" : "heuristic");
                } catch (err) {
                  toast.error(formatApiError(err));
                } finally {
                  setFinderLoading(false);
                }
              }} className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1 relative">
                  <Globe2 className="h-5 w-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"/>
                  <input
                    value={finderQuery}
                    onChange={(e) => setFinderQuery(e.target.value)}
                    placeholder="e.g. 'Acme Bakery'"
                    data-testid="onboard-finder-query"
                    autoFocus
                    className="w-full pl-12 pr-4 py-3.5 rounded-xl border-2 border-[#E2E8F0] focus:ring-4 focus:ring-[#F26953]/10 focus:border-[#F26953] outline-none text-base"
                  />
                </div>
                <button
                  type="submit"
                  disabled={finderLoading}
                  data-testid="onboard-finder-search"
                  className="inline-flex items-center justify-center gap-2 bg-[#F26953] text-white font-semibold px-6 py-3.5 rounded-xl hover:bg-[#D85340] disabled:opacity-60 shadow-sm"
                >
                  {finderLoading ? <Loader2 className="h-4 w-4 animate-spin"/> : <Sparkles className="h-4 w-4"/>}
                  {finderLoading ? "Searching…" : "Find domains"}
                </button>
              </form>

              {finderResults && finderResults.length > 0 && (
                <div className="space-y-2" data-testid="onboard-finder-results">
                  {finderSource === "rdap" && (
                    <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 inline-flex items-center gap-1.5 mb-2">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
                      Live availability via RDAP
                    </div>
                  )}
                  <ul className="divide-y divide-[#F1E5E1] rounded-2xl border border-[#F1E5E1] bg-white overflow-hidden">
                    {finderResults.map((r) => (
                      <li
                        key={r.domain}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-3 py-3 sm:px-4 sm:py-4 gap-3"
                        data-testid={`onboard-finder-result-${r.domain}`}
                      >
                        <div className="min-w-0 w-full sm:w-auto sm:flex-1">
                          <div
                            className="mono font-semibold text-[#0F172A] text-base sm:text-[15px] leading-snug"
                            style={{ wordBreak: "break-all", overflowWrap: "anywhere" }}
                            data-testid={`onboard-finder-name-${r.domain}`}
                          >
                            {r.domain}
                          </div>
                          <div className={`mt-1 text-xs font-semibold inline-flex items-center gap-1.5 ${r.available ? "text-emerald-600" : "text-slate-400"}`}>
                            <span className={`inline-block h-1.5 w-1.5 rounded-full ${r.available ? "bg-emerald-500" : "bg-slate-300"}`}></span>
                            {r.available ? "Available" : "Registered"}
                          </div>
                        </div>
                        <a
                          href={r.register_url}
                          target="_blank"
                          rel="noreferrer"
                          onClick={() => {
                            // Pre-fill step 2 with this domain + advance
                            setDomain(r.domain);
                            setTimeout(() => setStep(2), 300);
                          }}
                          data-testid={`onboard-finder-buy-${r.domain}`}
                          className={`w-full sm:w-auto inline-flex items-center justify-center gap-1.5 text-sm font-semibold px-4 py-2.5 sm:py-2 rounded-lg transition-colors whitespace-nowrap flex-shrink-0 ${
                            r.available
                              ? "bg-[#F26953] text-white hover:bg-[#D85340]"
                              : "bg-white border border-[#E2E8F0] text-[#475569] hover:bg-slate-50"
                          }`}
                        >
                          <span className="sm:hidden">{r.available ? "Register" : "Check"}</span>
                          <span className="hidden sm:inline">{r.available ? "Register on Dynadot" : "Check on Dynadot"}</span>
                          <ExternalLink className="h-3.5 w-3.5"/>
                        </a>
                      </li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-slate-500 pt-1">
                    After you register, come back to this tab — we'll have your domain pre-filled and ready. Already registered one? <button type="button" onClick={() => setStep(2)} className="text-[#F26953] font-semibold hover:underline" data-testid="onboard-finder-continue">Continue to next step →</button>
                  </p>
                </div>
              )}

              <div className="flex justify-between pt-2">
                <button onClick={() => setStep(1)} data-testid="onboard-back" className="text-sm text-[#475569] font-medium px-4 py-2 rounded-lg hover:bg-slate-100">
                  Back
                </button>
                {domain && (
                  <button onClick={() => setStep(2)} data-testid="onboard-finder-use" className="inline-flex items-center gap-2 bg-[#F26953] text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-[#D85340]">
                    Continue with <span className="mono text-xs">{domain}</span> <ArrowRight className="h-4 w-4"/>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* STEP 2 — Domain entry */}
          {step === 2 && (
            <div className="p-10 space-y-6" data-testid="onboard-step-domain">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A] tracking-tight">What's your domain?</h1>
                <p className="text-sm text-[#475569] mt-2">The one you bought for your business, like <span className="mono">yourbusiness.com</span>.</p>
              </div>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Domain</span>
                <div className="mt-2 relative">
                  <Globe2 className="h-5 w-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"/>
                  <input
                    value={domain}
                    onChange={(e) => setDomain(e.target.value.toLowerCase())}
                    placeholder="yourbusiness.com"
                    data-testid="onboard-domain-input"
                    autoFocus
                    className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-[#E2E8F0] focus:ring-4 focus:ring-[#F26953]/10 focus:border-[#F26953] outline-none text-lg mono"
                  />
                </div>
              </label>
              <div className="flex justify-between">
                <button onClick={() => setStep(1)} data-testid="onboard-back" className="text-sm text-[#475569] font-medium px-4 py-2 rounded-lg hover:bg-slate-100">
                  Back
                </button>
                <button
                  onClick={() => {
                    if (!domain.match(/^[a-z0-9.-]+\.[a-z]{2,}$/i)) {
                      toast.error("Enter a valid domain like example.com"); return;
                    }
                    setStep(3);
                  }}
                  data-testid="onboard-continue"
                  className="inline-flex items-center gap-2 bg-[#F26953] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#D85340] shadow-sm"
                >
                  Continue <ArrowRight className="h-4 w-4"/>
                </button>
              </div>
            </div>
          )}

          {/* STEP 3 — Aliases */}
          {step === 3 && (
            <div className="p-10 space-y-6" data-testid="onboard-step-aliases">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A] tracking-tight">Pick your email addresses</h1>
                <p className="text-sm text-[#475569] mt-2">These will all forward to the same inbox. Add as many as you like.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {DEFAULT_ALIASES.map((a) => {
                  const active = aliasChips.has(a);
                  return (
                    <button
                      key={a}
                      type="button"
                      onClick={() => toggleAlias(a)}
                      data-testid={`alias-chip-${a}`}
                      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full border-2 font-mono text-sm transition-all ${
                        active
                          ? "border-[#F26953] bg-[#FEE7E1] text-[#0F172A] font-semibold"
                          : "border-[#E2E8F0] bg-white text-[#475569] hover:border-slate-300"
                      }`}
                    >
                      {active && <Check className="h-3.5 w-3.5 text-[#F26953]"/>}
                      {a}@<span className="text-slate-400 hidden sm:inline">{domain}</span>
                    </button>
                  );
                })}
                {/* Custom aliases shown as chips too */}
                {Array.from(aliasChips).filter(a => !DEFAULT_ALIASES.includes(a)).map((a) => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => toggleAlias(a)}
                    data-testid={`alias-chip-custom-${a}`}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border-2 border-[#F26953] bg-[#FEE7E1] text-[#0F172A] font-semibold font-mono text-sm"
                  >
                    <Check className="h-3.5 w-3.5 text-[#F26953]"/>
                    {a}@
                    <X className="h-3.5 w-3.5 ml-1 text-slate-400 hover:text-red-500"/>
                  </button>
                ))}
              </div>
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Or add your own</span>
                <div className="mt-2 flex gap-2">
                  <div className="flex-1 flex items-center rounded-lg border-2 border-[#E2E8F0] focus-within:border-[#F26953] overflow-hidden">
                    <input
                      value={customAlias}
                      onChange={(e) => setCustomAlias(e.target.value.toLowerCase())}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomAlias(); } }}
                      placeholder="matthew"
                      data-testid="onboard-custom-alias-input"
                      className="flex-1 px-4 py-2.5 text-sm mono outline-none"
                    />
                    <span className="px-3 text-sm text-slate-500 bg-slate-50 border-l border-[#E2E8F0]">@{domain}</span>
                  </div>
                  <button
                    type="button"
                    onClick={addCustomAlias}
                    data-testid="onboard-custom-alias-add"
                    className="bg-[#0F172A] text-white font-semibold px-4 rounded-lg hover:bg-slate-800 text-sm inline-flex items-center gap-1"
                  >
                    <Plus className="h-4 w-4"/> Add
                  </button>
                </div>
              </div>
              <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-3">
                <span className="font-semibold text-[#0F172A]">{aliasChips.size}</span> address{aliasChips.size !== 1 ? "es" : ""} selected. You can add more later from your dashboard.
              </div>

              {/* Plan-limit warning */}
              {user?.role !== "admin" && aliasChips.size > aliasLimit && (
                <div
                  data-testid="plan-limit-warning"
                  className="rounded-2xl border-2 border-[#F26953]/30 bg-gradient-to-br from-[#FEE7E1] to-white p-5"
                >
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-xl bg-[#F26953] grid place-items-center flex-shrink-0">
                      <Sparkles className="h-5 w-5 text-white"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold tracking-[0.15em] uppercase text-[#F26953] mb-0.5">
                        You need a bigger plan
                      </div>
                      <h3 className="text-base font-bold text-[#0F172A]">
                        {planLabel} plan includes {aliasLimit} address{aliasLimit !== 1 ? "es" : ""}. You've picked {aliasChips.size}.
                      </h3>
                      <p className="text-xs text-[#475569] mt-1">
                        {planId === "starter" && "Upgrade to Growth ($6/mo) for 5 addresses, or Pro ($9/mo) for unlimited."}
                        {planId === "growth" && "Upgrade to Pro ($9/mo) for unlimited addresses."}
                        {" "}We'll only create the first {aliasLimit} below — the rest can be added after upgrading.
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {planId === "starter" && (
                          <>
                            <button
                              type="button"
                              data-testid="plan-limit-upgrade-growth"
                              onClick={async () => {
                                try {
                                  const { data } = await api.post("/billing/checkout", {
                                    package_id: "growth_monthly",
                                    origin_url: window.location.origin,
                                  });
                                  if (data?.url) window.location.href = data.url;
                                } catch (e) { toast.error(formatApiError(e)); }
                              }}
                              className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-slate-800"
                            >
                              <Zap className="h-3 w-3"/> Upgrade to Growth — $6/mo
                            </button>
                            <button
                              type="button"
                              data-testid="plan-limit-upgrade-pro"
                              onClick={async () => {
                                try {
                                  const { data } = await api.post("/billing/checkout", {
                                    package_id: "pro_monthly",
                                    origin_url: window.location.origin,
                                  });
                                  if (data?.url) window.location.href = data.url;
                                } catch (e) { toast.error(formatApiError(e)); }
                              }}
                              className="inline-flex items-center gap-1.5 bg-[#F26953] text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-[#D85340]"
                            >
                              <Sparkles className="h-3 w-3"/> Go Pro — $9/mo (unlimited)
                            </button>
                          </>
                        )}
                        {planId === "growth" && (
                          <button
                            type="button"
                            data-testid="plan-limit-upgrade-pro"
                            onClick={async () => {
                              try {
                                const { data } = await api.post("/billing/checkout", {
                                  package_id: "pro_monthly",
                                  origin_url: window.location.origin,
                                });
                                if (data?.url) window.location.href = data.url;
                              } catch (e) { toast.error(formatApiError(e)); }
                            }}
                            className="inline-flex items-center gap-1.5 bg-[#F26953] text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-[#D85340]"
                          >
                            <Sparkles className="h-3 w-3"/> Go Pro — $9/mo (unlimited)
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex justify-between">
                <button onClick={() => setStep(2)} data-testid="onboard-back" className="text-sm text-[#475569] font-medium px-4 py-2 rounded-lg hover:bg-slate-100">Back</button>
                <button
                  onClick={() => {
                    if (aliasChips.size === 0) { toast.error("Pick at least one address"); return; }
                    setStep(4);
                  }}
                  data-testid="onboard-continue"
                  className="inline-flex items-center gap-2 bg-[#F26953] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#D85340] shadow-sm"
                >
                  Continue <ArrowRight className="h-4 w-4"/>
                </button>
              </div>
            </div>
          )}

          {/* STEP 4 — Destination */}
          {step === 4 && (
            <div className="p-10 space-y-6" data-testid="onboard-step-destination">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A] tracking-tight">Where should emails go?</h1>
                <p className="text-sm text-[#475569] mt-2">We'll forward everything to your existing inbox. You keep using what you already know.</p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {DESTINATIONS.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => setDestinationId(d.id)}
                    data-testid={`destination-${d.id}`}
                    className={`p-4 rounded-xl border-2 font-semibold text-sm transition-all ${
                      destinationId === d.id
                        ? "border-[#F26953] bg-[#FEE7E1] text-[#0F172A]"
                        : "border-[#E2E8F0] bg-white text-[#475569] hover:border-slate-300"
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>

              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Your email address</span>
                <div className="mt-2 relative">
                  <Mail className="h-5 w-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"/>
                  <input
                    type="email"
                    value={destinationEmail}
                    onChange={(e) => setDestinationEmail(e.target.value)}
                    placeholder="you@gmail.com"
                    data-testid="onboard-destination-email"
                    className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-[#E2E8F0] focus:ring-4 focus:ring-[#F26953]/10 focus:border-[#F26953] outline-none text-base mono"
                  />
                </div>
                <span className="text-[11px] text-slate-400 mt-2 block">We'll forward every email here — you don't need to change anything in your inbox.</span>
              </label>

              <div className="flex justify-between">
                <button onClick={() => setStep(3)} data-testid="onboard-back" className="text-sm text-[#475569] font-medium px-4 py-2 rounded-lg hover:bg-slate-100">Back</button>
                <button
                  onClick={() => {
                    if (!destinationEmail.match(/^[^@\s]+@[^@\s]+\.[^@\s]+$/)) {
                      toast.error("Enter a valid email address"); return;
                    }
                    setStep(5);
                  }}
                  data-testid="onboard-finish"
                  className="inline-flex items-center gap-2 bg-[#F26953] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#D85340] shadow-sm"
                >
                  Set it up <Zap className="h-4 w-4"/>
                </button>
              </div>
            </div>
          )}

          {/* STEP 5 — Loading */}
          {step === 5 && (
            <div className="p-16 text-center space-y-6" data-testid="onboard-step-loading">
              <div className="h-20 w-20 rounded-full bg-[#FEE7E1] grid place-items-center mx-auto">
                <Loader2 className="h-10 w-10 text-[#F26953] animate-spin"/>
              </div>
              <div className="space-y-2">
                {LOADING_STEPS.map((s, i) => (
                  <div
                    key={i}
                    className={`text-base transition-all ${
                      i < loadingStepIdx ? "text-slate-400 line-through" :
                      i === loadingStepIdx ? "text-[#0F172A] font-bold" :
                      "text-slate-300"
                    }`}
                  >
                    {i < loadingStepIdx && <Check className="h-4 w-4 inline mr-1 text-emerald-500"/>}
                    {s.label}
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400">This usually takes 10–20 seconds.</p>
            </div>
          )}

          {/* STEP 6 — Success */}
          {step === 6 && (
            <div className="p-10 space-y-6 text-center" data-testid="onboard-step-success">
              <div className="h-20 w-20 rounded-2xl bg-emerald-100 grid place-items-center mx-auto">
                <PartyPopper className="h-10 w-10 text-emerald-600"/>
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-[#0F172A] tracking-tight">Your professional email is ready!</h1>
                <p className="text-base text-[#475569] mt-3">
                  {createdAliases.length} address{createdAliases.length !== 1 ? "es" : ""} set up and forwarding to <span className="mono font-semibold text-[#0F172A]">{destinationEmail}</span>
                </p>
              </div>

              {createdAliases.length > 0 && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5 text-left space-y-2">
                  {createdAliases.map((a) => (
                    <div key={a.id} className="flex items-center gap-3 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0"/>
                      <span className="mono font-semibold text-[#0F172A]">{a.alias}@{a.domain}</span>
                      <ArrowRight className="h-3 w-3 text-slate-400"/>
                      <span className="mono text-slate-500 truncate">{destinationEmail}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {dest.helpUrl && (
                  <a
                    href={dest.helpUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="onboard-send-as-guide"
                    className="rounded-xl bg-[#0F172A] text-white font-semibold p-4 hover:bg-slate-800 transition-colors inline-flex items-center justify-center gap-2"
                  >
                    <Mail className="h-4 w-4"/>
                    Send mail as · {dest.label} setup <ExternalLink className="h-3 w-3"/>
                  </a>
                )}
                <a
                  href={`mailto:${primaryEmail}?subject=Testing%20my%20new%20email&body=Hi%20—%20this%20is%20a%20test%20to%20make%20sure%20my%20new%20email%20works.`}
                  data-testid="onboard-test-email"
                  className="rounded-xl bg-white border-2 border-[#E2E8F0] text-[#0F172A] font-semibold p-4 hover:border-[#F26953]/40 inline-flex items-center justify-center gap-2"
                >
                  <Zap className="h-4 w-4 text-[#F26953]"/>
                  Send a test email
                </a>
              </div>

              <button
                onClick={() => nav("/dashboard")}
                data-testid="onboard-done"
                className="mt-4 text-sm font-semibold text-[#475569] hover:text-[#0F172A] inline-flex items-center gap-1"
              >
                Go to dashboard <ArrowRight className="h-4 w-4"/>
              </button>
            </div>
          )}

          {/* STEP 7 — DNS fallback (only when auto-verify fails) */}
          {step === 7 && (
            <div className="p-10 space-y-6" data-testid="onboard-step-dns-fallback">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-xl bg-amber-100 grid place-items-center flex-shrink-0">
                  <ShieldCheck className="h-6 w-6 text-amber-600"/>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight">One final step to activate your email</h1>
                  <p className="text-sm text-[#475569] mt-2">
                    Your addresses are created — we just need to tell <span className="mono font-semibold">{domain}</span> to route mail to us. Paste <strong>all</strong> of the records below where you bought your domain. Mailgun requires the full set to be valid before it will accept mail (otherwise you'll see a "550 Relaying denied" bounce).
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">1. Routing · MX records</span>
                  <span className="text-[10px] text-slate-400">(receives mail)</span>
                </div>
                {(dnsInfo?.receiving || []).map((r, i) => (
                  <CopyRow
                    key={`rx-${i}`}
                    label={`${r.type || "MX"} · priority ${r.priority || 10}${r.valid ? " · ✓ valid" : ""}`}
                    value={r.value}
                    testId={`dns-mx-${i}`}
                  />
                ))}
                {(dnsInfo?.receiving || []).length === 0 && (
                  <>
                    <CopyRow label="MX · priority 10" value="mxa.mailgun.org" testId="dns-mx-0"/>
                    <CopyRow label="MX · priority 10" value="mxb.mailgun.org" testId="dns-mx-1"/>
                  </>
                )}
              </div>

              {(dnsInfo?.sending || []).length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">2. Authentication · SPF, DKIM &amp; tracking</span>
                    <span className="text-[10px] text-slate-400">(required for activation)</span>
                  </div>
                  {(dnsInfo?.sending || []).map((r, i) => (
                    <div key={`tx-${i}`} className="space-y-1">
                      <div className="text-[11px] text-slate-500">
                        <span className="font-semibold text-[#0F172A]">{r.type}</span>
                        {" · "}<span className="mono">{r.name}</span>
                        {r.valid && <span className="ml-2 text-emerald-600 font-semibold">✓ valid</span>}
                      </div>
                      <CopyRow label={r.type} value={r.value} testId={`dns-tx-${i}`}/>
                    </div>
                  ))}
                </div>
              )}

              <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-4 space-y-1">
                <p><strong className="text-[#0F172A]">Not sure where to paste these?</strong></p>
                <p>Log into wherever you bought <span className="mono">{domain}</span> (Dynadot, Cloudflare, Namecheap, GoDaddy) → look for "DNS" or "Email settings" → add each record above with the exact type, name, and value shown.</p>
                <p className="text-slate-400">Changes can take 5–15 minutes to take effect. We'll re-check automatically every hour, or press the button below to check now.</p>
              </div>

              <div className="flex justify-between gap-3">
                <button
                  onClick={() => nav("/dashboard")}
                  data-testid="onboard-dns-later"
                  className="text-sm text-[#475569] font-medium px-4 py-2 rounded-lg hover:bg-slate-100"
                >
                  I'll finish this later
                </button>
                <button
                  onClick={retryVerify}
                  disabled={retrying}
                  data-testid="onboard-dns-verify"
                  className="inline-flex items-center gap-2 bg-[#F26953] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#D85340] disabled:opacity-60"
                >
                  {retrying ? <Loader2 className="h-4 w-4 animate-spin"/> : <CheckCircle2 className="h-4 w-4"/>}
                  {retrying ? "Checking…" : "I've added them — check now"}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
